<!-- eslint-disable perfectionist/sort-modules -->
<!-- eslint-disable perfectionist/sort-imports -->
<!-- eslint-disable no-unused-vars -->
<script setup lang="ts">
import {
  ArrowLeft, Check, Pencil, X,
} from 'lucide-vue-next';
import {
  computed, ref, watch,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { formatCellphoneMasked, normalizeCellphoneDigits } from '@solfacil/frontend-utils/cellphone';
import { formatCnpjMasked, normalizeCnpjAlphanumeric } from '@solfacil/frontend-utils/cnpj';

import DashboardLayout from '~/layouts/DashboardLayout.vue';
import { DATE_FORMATS } from '~/constants';
import { PersonAPI } from '~/services/person-api/person-api.types';
import date from '~/utils/date';

import PartnerAssociatesSection from '../components/PartnerAssociatesSection.vue';
import PartnerCredentialsSection from '../components/PartnerCredentialsSection.vue';
import PartnerDocumentsList from '../components/PartnerDocumentsList.vue';
import PartnerInstallationBypassSection from '../components/PartnerInstallationBypassSection.vue';
import PartnerUsersSection from '../components/PartnerUsersSection.vue';
import useDownloadOperationalAgreement from '../queries/useDownloadOperationalAgreement';
import usePartner from '../queries/usePartner';
import corporateNameSchema from '../schemas/corporateNameSchema';
import buildPartnerDocuments from '../utils/partnerDocuments';
import { useStartEscrowAccount, useUpdateCompany } from '~/store/registerPartner/useCompanyMutations';
import useRegisterPartnerDetails from '~/store/registerPartner/useRegisterPartnerDetails';

const route = useRoute();
const router = useRouter();

const partnerUsersSectionRef = ref<InstanceType<typeof PartnerUsersSection> | null>(null);
const usersPageSize = computed(() => partnerUsersSectionRef.value?.pageSize ?? 10);

const partnerId = computed(() => Number((route.params as { id?: string })?.id ?? '0'));

const { data: registerPartner } = useRegisterPartnerDetails(partnerId);
const updateCompanyMutation = useUpdateCompany();
const startEscrowMutation = useStartEscrowAccount();

const isEditingCorporateName = ref(false);
const editCorporateName = ref('');
const displayCorporateName = ref('');

const corporateNameValidation = computed(() => (
  corporateNameSchema.safeParse(editCorporateName.value)
));

const corporateNameError = computed(() => {
  if (!isEditingCorporateName.value || corporateNameValidation.value.success) {
    return null;
  }
  return corporateNameValidation.value.error.issues[0]?.message ?? 'Campo inválido';
});

const canSaveCorporateName = computed(() => corporateNameValidation.value.success);

const PROFILE_LABELS: Record<string, string> = {
  basic: 'Básico',
  essential: 'Essencial',
  stellar: 'Estelar',
};

const COMPANY_STATUS_LABELS: Record<string, string> = {
  blocked: 'Bloqueado',
  compliance: 'Compliance',
  create_access: 'Criando Acesso',
  create_escrow_account: 'Abertura de Conta',
  done: 'Concluído',
  financial_health: 'Saúde Financeira',
  onboarding: 'Onboarding',
  temporarily_blocked: 'Bloqueado Temporariamente',
};

const {
  data: partner,
  error: partnerError,
  isLoading: isLoadingPartner,
} = usePartner(partnerId);

watch(
  () => registerPartner.value?.official_name,
  (name) => {
    if (isEditingCorporateName.value) return;

    if (name) {
      displayCorporateName.value = name;
      document.title = `Detalhes - ${name}`;
      return;
    }

    document.title = 'Detalhes do Parceiro';
  },
  { immediate: true },
);

const corporateNameForDisplay = computed(() => (
  displayCorporateName.value || registerPartner.value?.official_name || ''
));

function formatProfile(profile?: string): string {
  return profile ? (PROFILE_LABELS[profile] ?? profile) : 'Não informado';
}

function formatCompanyStatus(status?: string): string {
  return status ? (COMPANY_STATUS_LABELS[status] ?? status) : 'Não informado';
}

function startEditCorporateName() {
  editCorporateName.value = corporateNameForDisplay.value;
  isEditingCorporateName.value = true;
}

function cancelEditCorporateName() {
  isEditingCorporateName.value = false;
  editCorporateName.value = '';
}

async function saveCorporateName() {
  const companyId = registerPartner.value?.company_id;
  const validated = corporateNameValidation.value;
  if (!companyId || !validated.success) return;

  const newCorporateName = validated.data;

  try {
    await updateCompanyMutation.mutateAsync({
      companyId,
      officialName: newCorporateName,
      partnerId: partnerId.value,
    });

    displayCorporateName.value = newCorporateName;
    document.title = `Detalhes - ${newCorporateName}`;
    isEditingCorporateName.value = false;
    editCorporateName.value = '';
  } catch {
    // handled by mutation
  }
}

async function handleStartEscrowAccount() {
  const companyId = registerPartner.value?.company_id;
  if (!companyId) return;

  try {
    await startEscrowMutation.mutateAsync(companyId);
  } catch {
    // handled by mutation
  }
}

const isOperationTermAccepted = computed(() => !!partner.value?.operation_term_accepted);

const operatingAgreementQuery = useDownloadOperationalAgreement(
  partnerId.value,
  isOperationTermAccepted,
);

const partnerDocuments = computed(() => buildPartnerDocuments({
  operatingAgreement: operatingAgreementQuery.data.value,
  operationTermAccepted: isOperationTermAccepted.value,
  socialContract: registerPartner.value?.social_contract,
}));

const isLoadingPartnerDocuments = computed(() => (
  isOperationTermAccepted.value && operatingAgreementQuery.isFetching.value
));

function formatBoolean(value: boolean): string {
  return value ? 'Sim' : 'Não';
}

function formatDate(dateString?: string): string {
  if (!dateString) return 'Não informado';
  return date(dateString).format(DATE_FORMATS.DATETIME);
}

function formatLevel(sfplus?: PersonAPI.Sfplus): string {
  if (!sfplus?.level) return 'Não definido';
  return sfplus.level >= 99 ? 'Black' : `Level ${sfplus.level}`;
}

function goBack() {
  router.push('/partners');
}
</script>

<template>
  <DashboardLayout
    :title="corporateNameForDisplay || 'Carregando...'"
    description="Detalhes do parceiro"
  >
    <div class="row mb-3">
      <div class="col-12">
        <button
          type="button"
          class="btn btn-outline-secondary"
          @click="goBack"
        >
          <ArrowLeft class="icon icon-sm me-1" />
          Voltar para lista
        </button>
      </div>
    </div>

    <div v-if="isLoadingPartner" class="row row-cards">
      <div v-for="i in 6" :key="i" class="col-md-6 col-lg-4">
        <div class="card">
          <div class="card-body">
            <div class="placeholder-glow">
              <div class="placeholder col-8 mb-2" />
              <div class="placeholder col-12" />
              <div class="placeholder col-6" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else-if="partnerError" class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body text-center">
            <h3 class="text-danger">Erro ao carregar parceiro</h3>
            <p class="text-secondary">Não foi possível carregar os detalhes do parceiro.</p>
            <button type="button" class="btn btn-primary" @click="goBack">
              Voltar para lista
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-else-if="partner" class="row row-cards">
      <div class="col-12 mb-3">
        <div class="card">
          <div class="card-header">
            <h3 class="card-title">Informações Básicas</h3>
            <div
              v-if="registerPartner?.company_status === 'create_escrow_account'"
              class="card-options"
            >
              <button
                type="button"
                class="btn btn-primary btn-sm"
                :disabled="startEscrowMutation.isPending.value"
                @click="handleStartEscrowAccount"
              >
                <span
                  v-if="startEscrowMutation.isPending.value"
                  class="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                />
                Reenviar abertura de conta
              </button>
            </div>
          </div>
          <div class="card-body">
            <div class="row">
              <div class="col-md-6 col-lg-4">
                <div class="mb-4">
                  <label class="form-label fw-bold fs-3" for="editCorporateName">Razão Social</label>
                  <div v-if="isEditingCorporateName">
                    <input
                      id="editCorporateName"
                      v-model="editCorporateName"
                      type="text"
                      class="form-control"
                      :class="{ 'is-invalid': corporateNameError }"
                      :disabled="updateCompanyMutation.isPending.value"
                      @keydown.enter="saveCorporateName"
                      @keydown.escape="cancelEditCorporateName"
                    />
                    <div
                      v-if="corporateNameError"
                      class="invalid-feedback d-block"
                      role="alert"
                    >
                      {{ corporateNameError }}
                    </div>
                    <div class="d-flex gap-2 mt-2">
                      <button
                        type="button"
                        class="btn btn-success"
                        :disabled="updateCompanyMutation.isPending.value || !canSaveCorporateName"
                        @click="saveCorporateName"
                      >
                        <span
                          v-if="updateCompanyMutation.isPending.value"
                          class="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        />
                        <Check v-else :size="16" class="me-1" />
                        Salvar
                      </button>
                      <button
                        type="button"
                        class="btn btn-outline-secondary"
                        :disabled="updateCompanyMutation.isPending.value"
                        @click="cancelEditCorporateName"
                      >
                        <X :size="16" class="me-1" />
                        Cancelar
                      </button>
                    </div>
                  </div>
                  <div v-else class="d-flex align-items-center gap-3">
                    <p class="text-secondary mb-0 fs-3">{{ corporateNameForDisplay }}</p>
                    <button
                      type="button"
                      class="btn btn-outline-secondary d-inline-flex align-items-center
                        justify-content-center px-3 py-2"
                      title="Editar razão social"
                      aria-label="Editar razão social"
                      @click="startEditCorporateName"
                    >
                      <Pencil :size="18" />
                    </button>
                  </div>
                </div>
              </div>
              <div class="col-md-6 col-lg-4">
                <div class="mb-4">
                  <label class="form-label fw-bold fs-3">Nome Fantasia</label>
                  <p class="text-secondary mb-0 fs-3">{{ partner.trade_name || 'Não informado' }}</p>
                </div>
              </div>
              <div class="col-md-6 col-lg-3">
                <div class="mb-3">
                  <label class="form-label fw-bold fs-3">CNPJ</label>
                  <p class="text-secondary mb-0 fs-3">
                    {{ formatCnpjMasked(normalizeCnpjAlphanumeric(partner.document)) }}
                  </p>
                </div>
              </div>
              <div class="col-md-6 col-lg-3">
                <div class="mb-3">
                  <label class="form-label fw-bold fs-3">Inscrição Estadual</label>
                  <p class="text-secondary mb-0 fs-3">{{ partner.state_registration || 'Não informado' }}</p>
                </div>
              </div>
              <div class="col-md-6 col-lg-3">
                <div class="mb-3">
                  <label class="form-label fw-bold fs-3">Categoria</label>
                  <p class="text-secondary mb-0 fs-3">{{ partner.category || 'Não informado' }}</p>
                </div>
              </div>
              <div class="col-md-6 col-lg-3">
                <div class="mb-3">
                  <label class="form-label fw-bold fs-3">Perfil</label>
                  <p class="text-secondary mb-0 fs-3">{{ formatProfile(registerPartner?.profile) }}</p>
                </div>
              </div>
              <div class="col-md-6 col-lg-3">
                <div class="mb-3">
                  <label class="form-label fw-bold fs-3">Status da Empresa</label>
                  <p class="text-secondary mb-0 fs-3">
                    {{ formatCompanyStatus(registerPartner?.company_status) }}
                  </p>
                </div>
              </div>
              <div class="col-md-6 col-lg-3">
                <div class="mb-0">
                  <label class="form-label fw-bold fs-3">Status</label>
                  <span :class="`badge bg-${partner.active ? 'success' : 'danger'}-lt fs-3`">
                    {{ partner.active ? 'Ativo' : 'Inativo' }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-12 mb-3">
        <div class="card">
          <div class="card-header">
            <h3 class="card-title">Contato do Parceiro</h3>
          </div>
          <div class="card-body">
            <div class="row">
              <div class="col-md-6 col-lg-4">
                <div class="mb-3">
                  <label class="form-label fw-bold fs-3">E-mail Principal</label>
                  <p class="text-secondary mb-0 fs-3">{{ partner.contact.email }}</p>
                </div>
              </div>
              <div class="col-md-6 col-lg-4">
                <div class="mb-3">
                  <label class="form-label fw-bold fs-3">Telefone Móvel</label>
                  <p class="text-secondary mb-0 fs-3">
                    {{ partner.contact.phone
                      ? formatCellphoneMasked(normalizeCellphoneDigits(partner.contact.phone))
                      : 'Não informado' }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-12 mb-3">
        <div class="card">
          <div class="card-header">
            <h3 class="card-title">Endereço do Parceiro</h3>
          </div>
          <div class="card-body">
            <div class="row">
              <div class="col-md-6 col-lg-4">
                <div class="mb-3">
                  <label class="form-label fw-bold fs-3">CEP</label>
                  <p class="text-secondary mb-0 fs-3">{{ partner.address.zipcode || 'Não informado' }}</p>
                </div>
              </div>
              <div class="col-md-6 col-lg-4">
                <div class="mb-3">
                  <label class="form-label fw-bold fs-3">Logradouro</label>
                  <p class="text-secondary mb-0 fs-3">{{ partner.address.street || 'Não informado' }}</p>
                </div>
              </div>
              <div class="col-md-6 col-lg-4">
                <div class="mb-3">
                  <label class="form-label fw-bold fs-3">Número</label>
                  <p class="text-secondary mb-0 fs-3">{{ partner.address.number || 'Não informado' }}</p>
                </div>
              </div>
              <div class="col-md-6 col-lg-4">
                <div class="mb-3">
                  <label class="form-label fw-bold fs-3">Complemento</label>
                  <p class="text-secondary mb-0 fs-3">{{ partner.address.complement || 'Não informado' }}</p>
                </div>
              </div>
              <div class="col-md-6 col-lg-4">
                <div class="mb-3">
                  <label class="form-label fw-bold fs-3">Bairro</label>
                  <p class="text-secondary mb-0 fs-3">{{ partner.address.district || 'Não informado' }}</p>
                </div>
              </div>
              <div class="col-md-6 col-lg-4">
                <div class="mb-3">
                  <label class="form-label fw-bold fs-3">Cidade</label>
                  <p class="text-secondary mb-0 fs-3">{{ partner.address.city || 'Não informado' }}</p>
                </div>
              </div>
              <div class="col-md-6 col-lg-4">
                <div class="mb-0">
                  <label class="form-label fw-bold fs-3">Estado</label>
                  <p class="text-secondary mb-0 fs-3">{{ partner.address.state || 'Não informado' }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-12 mb-3">
        <div class="card">
          <div class="card-header">
            <h3 class="card-title">
              Responsável da Solfacil - <small class="text-muted fw-normal">(Gerente de contas Solfácil)</small>
            </h3>
          </div>
          <div class="card-body">
            <div class="row">
              <div class="col-md-6 col-lg-3">
                <div class="mb-3">
                  <label class="form-label fw-bold fs-3">Nome</label>
                  <p class="text-secondary mb-0 fs-3">{{ partner.responsible.fullname }}</p>
                </div>
              </div>
              <div class="col-md-6 col-lg-3">
                <div class="mb-3">
                  <label class="form-label fw-bold fs-3">E-mail</label>
                  <p class="text-secondary mb-0 fs-3">{{ partner.responsible.contact.email || 'Não informado' }}</p>
                </div>
              </div>
              <div class="col-md-6 col-lg-3">
                <div class="mb-3">
                  <label class="form-label fw-bold fs-3">Celular</label>
                  <p class="text-secondary mb-0 fs-3">
                    {{ partner.responsible.contact.mobile
                      ? formatCellphoneMasked(normalizeCellphoneDigits(partner.responsible.contact.mobile))
                      : 'Não informado' }}
                  </p>
                </div>
              </div>
              <div class="col-md-6 col-lg-3">
                <div class="mb-0">
                  <label class="form-label fw-bold fs-3">Status</label>
                  <span :class="`badge bg-${partner.responsible.active ? 'success' : 'danger'}-lt fs-3`">
                    {{ partner.responsible.active ? 'Ativo' : 'Inativo' }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <PartnerUsersSection
        ref="partnerUsersSectionRef"
        :partner-id="partnerId"
        :partner-profile="registerPartner?.profile"
      />

      <PartnerAssociatesSection
        :partner-id="partnerId"
        :company-id="registerPartner?.company_id"
        :users-page-size="usersPageSize"
      />

      <div class="col-12 mb-3">
        <div class="row">
          <div class="col-lg-6">
            <div class="card h-100">
              <div class="card-header">
                <h3 class="card-title">Solfacil Plus</h3>
              </div>
              <div class="card-body">
                <div class="row">
                  <div class="col-md-6">
                    <div class="mb-3">
                      <label class="form-label fw-bold fs-3">Level</label>
                      <span class="badge bg-neutral-lt fs-3">
                        {{ formatLevel(partner.sfplus) }}
                      </span>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="mb-3">
                      <label class="form-label fw-bold fs-3">Pontos</label>
                      <p class="text-secondary mb-0 fs-3">
                        {{ partner.sfplus?.score?.current?.toLocaleString?.('pt-BR') || '0' }} Pontos
                      </p>
                    </div>
                  </div>

                </div>
              </div>
            </div>
          </div>

          <div class="col-lg-6">
            <div class="card h-100">
              <div class="card-header">
                <h3 class="card-title">Permissões</h3>
              </div>
              <div class="card-body">
                <div class="row">
                  <div class="mb-3 col-md-6 col-lg-4">
                    <label class="form-label fw-bold fs-3">Pode Simular?</label>
                    <span :class="`badge bg-${partner.permission.financing ? 'success' : 'secondary'}-lt fs-3`">
                      {{ formatBoolean(partner.permission.financing) }}
                    </span>
                  </div>
                  <div class="mb-0 col-md-6 col-lg-4">
                    <label class="form-label fw-bold fs-3">Pode Comprar na loja?</label>
                    <span :class="`badge bg-${partner.permission.store ? 'success' : 'secondary'}-lt fs-3`">
                      {{ formatBoolean(partner.permission.store) }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <PartnerCredentialsSection :partner-id="partnerId" />

      <PartnerInstallationBypassSection :partner-id="partnerId" />

      <div class="col-12">
        <div class="card">
          <div class="card-header">
            <h3 class="card-title">Informações Adicionais</h3>
          </div>
          <div class="card-body">
            <div class="row">
              <div class="col-md-6 col-lg-4">
                <div class="mb-3">
                  <label class="form-label fw-bold fs-3">Termo Operacional Aceito</label>
                  <span :class="`badge bg-${partner.operation_term_accepted ? 'success' : 'warning'}-lt fs-3`">
                    {{ formatBoolean(partner.operation_term_accepted) }}
                  </span>
                </div>
              </div>
              <div class="col-md-6 col-lg-4">
                <div class="mb-3">
                  <label class="form-label fw-bold fs-3">Data de Criação</label>
                  <p class="text-secondary mb-0 fs-3">{{ formatDate(partner.created_at) }}</p>
                </div>
              </div>
            </div>

            <div class="row">
              <div class="col-12">
                <label class="form-label fw-bold fs-3">Documentos</label>
                <PartnerDocumentsList
                  :documents="partnerDocuments"
                  :loading="isLoadingPartnerDocuments"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </DashboardLayout>
</template>

<style scoped lang="scss">
:deep(.card) {
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  }
}

:deep(.card-header) {
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  border-radius: 12px 12px 0 0 !important;
}

:deep(.card-title) {
  color: #495057;
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 0;
}

:deep(.form-label) {
  font-size: 0.875rem;
  color: #6c757d;
  margin-bottom: 0.25rem;
}

:deep(.badge) {
  font-size: 0.75rem;
  padding: 0.375rem 0.75rem;
}

:deep(.invalid-feedback.d-block) {
  display: block;
}
</style>
